/* eslint-disable no-console */
import {LeftOutlined, RightOutlined} from '@ant-design/icons';
import {Flex} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import {subscriptionPlanTypes} from '../../../../../constants/subscriptionConstants';
import {formatIndian} from '../../../../../utils/formatIndian';
import {getSubscriptionPlanType} from '../../../../../utils/SubscriptionHelper';
import {categoryWiseLabels} from '../constants/commonConstants';

import BenchmarkCard from './BenchmarkCard';
import {
  aboveBenchmarkTags,
  buildBenchmarkCards,
  totalEmissionsFromScope
} from './utils';

const CARDS_PER_SLIDE = 3;
const FREE_PLAN_LOCKED_CARD_KEYS = new Set([
  'scope 3 ghg emissions',
  'scope 3 ghg intensity',
  'total energy consumption',
  'Total quantity of waste generated'
]);

const IndustryBenchmarks: React.FC<{
  emissionsData: any;
  intensityData: any;
  consumptionData: any;
}> = ({emissionsData, intensityData, consumptionData}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollToCardId, setScrollToCardId] = useState<string | null>(null);

  const allCards = useMemo(
    () => buildBenchmarkCards(emissionsData, intensityData, consumptionData),
    [emissionsData, intensityData, consumptionData]
  );

  const totalEmissions = totalEmissionsFromScope(emissionsData);
  const isFreePlan = getSubscriptionPlanType() === subscriptionPlanTypes.FREE;

  const isCardLockedForFreePlan = (card: any): boolean => {
    if (!isFreePlan) return false;
    const cardId = `${card?.id ?? ''}`.trim().toLowerCase();
    const cardLabel = `${card?.label ?? ''}`.trim().toLowerCase();

    return (
      FREE_PLAN_LOCKED_CARD_KEYS.has(cardId) ||
      FREE_PLAN_LOCKED_CARD_KEYS.has(cardLabel)
    );
  };

  const aboveBenchmark = aboveBenchmarkTags(
    allCards.filter((card) => !isCardLockedForFreePlan(card))
  );

  const slideChunks: any[][] = [];
  for (let i = 0; i < allCards.length; i += CARDS_PER_SLIDE) {
    slideChunks.push(allCards.slice(i, i + CARDS_PER_SLIDE));
  }

  const totalSlides = slideChunks.length;
  const canGoPrev = currentSlide > 0;
  const canGoNext = currentSlide < totalSlides - 1 && totalSlides > 1;
  const currentChunk = slideChunks[currentSlide];

  const handleTagClick = (card: any) => {
    setScrollToCardId(card.id);
  };

  const handlePrev = () => {
    setCurrentSlide((s) => Math.max(0, s - 1));
  };

  const handleNext = () => {
    setCurrentSlide((s) => Math.min(totalSlides - 1, s + 1));
  };

  useEffect(() => {
    if (!scrollToCardId || allCards.length === 0) return;
    const cardIndex = allCards.findIndex((c) => c.id === scrollToCardId);
    if (cardIndex >= 0) {
      const slideIndex = Math.floor(cardIndex / CARDS_PER_SLIDE);
      setCurrentSlide(slideIndex);
      setScrollToCardId(null);
    }
  }, [scrollToCardId, allCards]);

  useEffect(() => {
    if (currentSlide >= totalSlides && totalSlides > 0) {
      setCurrentSlide(totalSlides - 1);
    }
  }, [currentSlide, totalSlides]);

  return (
    <>
      <div className="industryBenchmarksSection">
        <Flex className="industryBenchmarksLayout" gap={24}>
          <Flex
            className="industryBenchmarksSummary"
            vertical
            gap={24}
            justify="center"
          >
            <div className="industryBenchmarksTotalWrap">
              <span className="industryBenchmarksTotalLabel">
                Total Emissions
              </span>
              <span className="industryBenchmarksTotalValue">
                {formatIndian(Number(totalEmissions.toFixed(2)))}
                <span className="industryBenchmarksTotalUnit"> tCO2e</span>
              </span>
            </div>
            {aboveBenchmark.length > 0 && (
              <Flex vertical gap={8}>
                <span className="industryBenchmarksAboveLabel">
                  Above Benchmark:
                </span>
                <Flex wrap="wrap" gap={8}>
                  {aboveBenchmark.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      className="industryBenchmarkTag"
                      onClick={() => {
                        handleTagClick(card);
                      }}
                    >
                      {card.label}
                    </button>
                  ))}
                </Flex>
              </Flex>
            )}
          </Flex>

          <Flex className="industryBenchmarksCarouselWrap" vertical flex={1}>
            <Flex
              className="industryBenchmarksHeader"
              justify="space-between"
              vertical={false}
            >
              <Flex className="industryBenchmarksTitle">
                Industry Benchmarks vs Company
              </Flex>
              <Flex align="center" gap={8}>
                <div className="industryBenchmarksCategoryLabel">
                  {currentChunk?.[0]?.category
                    ? categoryWiseLabels[
                        currentChunk?.[0]
                          ?.category as keyof typeof categoryWiseLabels
                      ]
                    : ''}
                </div>
                <button
                  type="button"
                  className="industryBenchmarksNavBtn"
                  disabled={!canGoPrev}
                  onClick={handlePrev}
                  aria-label="Previous"
                >
                  <LeftOutlined />
                </button>
                <button
                  type="button"
                  className="industryBenchmarksNavBtn"
                  disabled={!canGoNext}
                  onClick={handleNext}
                  aria-label="Next"
                >
                  <RightOutlined />
                </button>
              </Flex>
            </Flex>
            <div className="industryBenchmarksCarouselInner">
              {allCards.length > 0 && (
                <div className="industryBenchmarksCarousel graph">
                  {currentChunk && (
                    <div className="industryBenchmarksSlide">
                      <Flex gap={16} wrap="nowrap" style={{width: '100%'}}>
                        {currentChunk.map((item) => (
                          <div
                            key={item.id}
                            className="industryBenchmarkCardWrap"
                          >
                            <BenchmarkCard
                              item={item}
                              isBlocked={isCardLockedForFreePlan(item)}
                            />
                          </div>
                        ))}
                        {currentChunk.length < CARDS_PER_SLIDE &&
                          Array.from({
                            length: CARDS_PER_SLIDE - currentChunk.length
                          }).map((_, i) => (
                            <div
                              key={`empty-${i}`}
                              className="industryBenchmarkCardWrap industryBenchmarkCardEmpty"
                            />
                          ))}
                      </Flex>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Flex>
        </Flex>
      </div>
    </>
  );
};

export default IndustryBenchmarks;
